import * as React from "react";

import {
  IContextualMenuClassNames,
  IContextualMenuProps,
} from "@fluentui/react";

import { UILocation } from "@1js/acui-common";
import { AppContext } from "@1js/acui-context";
import {
  convertMenuDefinitionToFabricMenuProps,
  emptyMenuProps,
  MenuDefinition,
} from "@1js/acui-menu";

import { AppFlyoutAnchorProps } from "./AppFlyoutAnchor.types";
import { AppSplitButtonProps } from "../../AppSplitButton/next";

export const useGetMenuProps = (
  props: AppFlyoutAnchorProps | AppSplitButtonProps,
  flyoutDropdownId: string,
  onMenuOpened: () => void,
  onSubMenuReady: (
    menu: MenuDefinition | undefined,
    flyoutMenuId: string | undefined
  ) => void,
  onCalloutReady: (menu: MenuDefinition | undefined) => void,
  onMenuDismissed: () => void
): ((menuDefinition: MenuDefinition | undefined) => IContextualMenuProps) => {
  const {
    controlInMenuRendererFunction,
    label,
    location,
    directionalHint,
    directionalHintFixed,
    useTargetAsMinWidth,
    calloutWidth,
    menuHostId,
    menuClassNames,
    preventCalloutDismissOnScroll,
  } = props;
  const context = React.useContext(AppContext);

  const getStyleSetForCallout = React.useCallback(
    (customMenuClassNames?: IContextualMenuClassNames) => {
      return customMenuClassNames &&
        customMenuClassNames.subComponentStyles &&
        customMenuClassNames.subComponentStyles.callout
        ? customMenuClassNames.subComponentStyles.callout
        : undefined;
    },
    []
  );

  // Method outputs a function to generate full menu props from menuDefinition. In case of deferred menus, the output can be passed into the menu
  // so that it can generate full menu props using menu definition it fetches from the store.
  const getMenuProps = React.useCallback(
    (menuDefinition: MenuDefinition | undefined) => {
      const baseMenuProps = menuDefinition
        ? convertMenuDefinitionToFabricMenuProps(
            {
              menuDefinition,
              controlInMenuRendererFunction,
              location: location as UILocation,
              onSubMenuPositioned: onSubMenuReady,
              customMenuClassNames: menuClassNames,
              arePerfImprovementsEnabled:
                context.isExperimentalFeatureEnabled?.(
                  "ContextualUIPerfImprovementsIsEnabled"
                ),
              onMenuPositioned: onCalloutReady,
            },
            directionalHintFixed
          )
        : emptyMenuProps;

      return {
        ...baseMenuProps,
        onMenuOpened,
        onMenuDismissed,
        directionalHint,
        useTargetAsMinWidth,
        ariaLabel: label,
        id: flyoutDropdownId,
        calloutProps: {
          ...baseMenuProps.calloutProps,
          layerProps: { hostId: menuHostId },
          calloutWidth,
          // Work round for fabric bug: https://github.com/OfficeDev/office-ui-fabric-react/issues/10710
          // subComponentStyles are not taken properly into callout menu styles
          // so we need to manually parse and inject styles here
          styles: getStyleSetForCallout(menuClassNames),
          preventDismissOnScroll: preventCalloutDismissOnScroll,
        },
      };
    },
    [
      context,
      controlInMenuRendererFunction,
      location,
      onSubMenuReady,
      menuClassNames,
      onCalloutReady,
      onMenuOpened,
      onMenuDismissed,
      directionalHint,
      directionalHintFixed,
      useTargetAsMinWidth,
      label,
      flyoutDropdownId,
      menuHostId,
      calloutWidth,
      getStyleSetForCallout,
      preventCalloutDismissOnScroll,
    ]
  );

  return getMenuProps;
};
